import * as React from "react";
import { DataTable } from "@/components/ui/datatable";
import { correctiveActionsColumnConfig } from "@/config/tableColumnConfig";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Audit, CorrectiveAction, Finding } from "@/app/pages/auditcentre";
import { CorrectiveActionDialog } from "./CorrectiveActionDialog";
import { useTenancy } from "@/contexts/TenancyContext";

interface StandaloneCorrectiveActionsTableProps {
  correctiveActions: CorrectiveAction[];
  audits: Audit[];
  onLink: (actionId: string, findingId: string) => Promise<void>;
  onUnlink: (actionId: string) => Promise<void>;
  // DED-1448: Add support for linking/unlinking to audits
  onLinkToAudit?: (actionId: string, auditId: string) => Promise<void>;
  onUnlinkFromAudit?: (actionId: string) => Promise<void>;
  onCreate: (action: Omit<CorrectiveAction, 'id' | 'createdAt' | 'updatedAt'>, findingId?: string) => Promise<void>;
  onUpdate: (id: string, action: Partial<CorrectiveAction>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  findings: Finding[];
  deepLinkedActionId?: string;
}

export function StandaloneCorrectiveActionsTable({
  correctiveActions,
  audits,
  onLink,
  onUnlink,
  onLinkToAudit,
  onUnlinkFromAudit,
  onCreate,
  onUpdate,
  onDelete,
  findings,
  deepLinkedActionId
}: StandaloneCorrectiveActionsTableProps) {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [actionToDelete, setActionToDelete] = React.useState<string | null>(null);
  const { userIdToName } = useTenancy();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [editingAction, setEditingAction] = React.useState<CorrectiveAction | null>(null);

  React.useEffect(() => {
    if (!deepLinkedActionId) return;
    const found = correctiveActions.find(a => a.id === deepLinkedActionId);
    if (found) {
      setEditingAction(found);
    }
  }, [deepLinkedActionId, correctiveActions]);

  const columns = [
    {
      accessorKey: "summary",
      header: "Summary",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => (
        <Badge variant={row.original.status === "Open" ? "destructiveSubtle" : "successSubtle"}>{row.original.status}</Badge>
      ),
    },
    {
      accessorKey: "owner",
      header: "Owner",
      cell: ({ row }) => <span>{userIdToName[row.original.owner] === "" || userIdToName[row.original.owner] === undefined ? row.original.owner : userIdToName[row.original.owner]}</span>,
    },
    {
      accessorKey: "dueDate",
      header: "Due Date",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const dueDate = row.original.dueDate;
        if (!dueDate) return <span className="text-muted-foreground">No due date</span>;
        
        // Handle different date formats
        let date: Date;
        if (typeof dueDate === 'string') {
          date = new Date(dueDate);
        } else if (dueDate instanceof Date) {
          date = dueDate;
        } else {
          // Handle FirebaseTimestamp
          try {
            date = dueDate.toDate();
          } catch (e) {
            return <span className="text-muted-foreground">Invalid date</span>;
          }
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return <span className="text-muted-foreground">Invalid date</span>;
        }
        
        return <span>{date.toLocaleDateString()}</span>;
      },
    },
    {
      accessorKey: "linkedTo",
      header: "Linked Finding",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const action = row.original as any;
        if (action.findingId && action.findingId !== "none") {
          const finding = findings.find(f => f.id === action.findingId);
          if (finding) {
            return <span title={finding.summary}>{finding.summary.substring(0, 30)}{finding.summary.length > 30 ? '...' : ''}</span>;
          }
          return "Linked to finding";
        }
        return <span className="text-muted-foreground">None</span>;
      },
    },
    // DED-1448: Add column to show linked audit
    {
      accessorKey: "linkedAudit",
      header: "Linked Audit",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const action = row.original as any;
        if (action.auditId) {
          const audit = audits.find(a => a.id === action.auditId);
          if (audit) {
            return <span title={audit.scope}>{audit.scope?.substring(0, 30)}{(audit.scope?.length || 0) > 30 ? '...' : ''}</span>;
          }
          return "Linked to audit";
        }
        return <span className="text-muted-foreground">None</span>;
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: { row: { original: CorrectiveAction } }) => {
        const action = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-gray-500">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                setEditingAction(action)
              }}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setActionToDelete(action.id);
                  setShowDeleteDialog(true);
                }}
                className="text-red-600"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Delete Corrective Action</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this corrective action? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (actionToDelete) {
                  onDelete(actionToDelete);
                }
                setShowDeleteDialog(false);
                setActionToDelete(null);
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setEditingAction(null);
              setIsCreateDialogOpen(true);
            }}
            size="sm"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Corrective Action
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={correctiveActions}
          pageSize={10}
          columnDisplayConfig={correctiveActionsColumnConfig}
          onRowClick={(row) => {
            setEditingAction(row);
          }}
        />

        <CorrectiveActionDialog
          open={isCreateDialogOpen || !!editingAction}
          onOpenChange={(open) => {
            if (!open) {
              setIsCreateDialogOpen(false);
              setEditingAction(null);
            }
          }}
          action={editingAction}
          onSave={async (data) => {
            if (editingAction) {
              await onUpdate(editingAction.id, data);
            } else {
              await onCreate(data);
            }
            setIsCreateDialogOpen(false);
            setEditingAction(null);
          }}
          findings={findings}
          audits={audits}
        />
      </div>
    </>
  );
}
